/**
 * liveStock.ts — Phase 2 client display projection for product + batch stock.
 *
 * Server is authoritative (Phase 1 derives product.currentStock and batch.quantity from
 * movements). On the client we render the freshest possible value across devices:
 *
 *     display = <server-authoritative value, captured on the last pull>
 *             + <THIS device's own not-yet-synced movement deltas>
 *
 * The two terms are disjoint: the server value already includes every SYNCED change from
 * all devices; the overlay adds only this device's UNSYNCED (pending) movements. So there
 * is no double-counting, and another device's concurrent change shows the moment we pull
 * it — without waiting for our own pending changes to sync.
 *
 * `product.currentStock` / `batch.quantity` (the optimistic local values) are left
 * untouched and remain the source of truth for local logic (e.g. availability checks).
 * This module only affects what stock-display surfaces render.
 *
 * Caveat (accepted, display-only): server snapshots live in memory, so a cold start
 * before the first pull falls back to the optimistic local value until the next sync.
 */
import { create } from 'zustand';
import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { StockMovement } from '@/db/models/Inventory';

// ── Canonical signs (mirror of backend stockDerivation). ──
// PRODUCT total: godown transfers net to zero (only DAMAGE decrements).
const PROD_INCREMENT = new Set(['IN', 'PURCHASE', 'PURCHASE_RECEIVE', 'OPENING']);
const PROD_DECREMENT = new Set(['OUT', 'RETURN', 'PURCHASE_RETURN', 'DAMAGE']);
const PROD_NEUTRAL = new Set(['TRANSFER_IN', 'TRANSFER_OUT']);
// BATCH level: transfers DO count (each leg carries its own batch_id).
const BATCH_INCREMENT = new Set(['IN', 'PURCHASE', 'PURCHASE_RECEIVE', 'TRANSFER_IN', 'OPENING']);
const BATCH_DECREMENT = new Set(['OUT', 'RETURN', 'PURCHASE_RETURN', 'DAMAGE', 'TRANSFER_OUT']);

export function productStockDelta(type: string, quantity: number): number {
  if (!Number.isFinite(quantity)) return 0;
  if (PROD_NEUTRAL.has(type)) return 0;
  if (PROD_INCREMENT.has(type)) return Math.abs(quantity);
  if (PROD_DECREMENT.has(type)) return -Math.abs(quantity);
  if (type === 'ADJUSTMENT') return quantity;
  return quantity;
}

export function batchStockDelta(type: string, quantity: number): number {
  if (!Number.isFinite(quantity)) return 0;
  if (BATCH_INCREMENT.has(type)) return Math.abs(quantity);
  if (BATCH_DECREMENT.has(type)) return -Math.abs(quantity);
  if (type === 'ADJUSTMENT') return quantity;
  return quantity;
}

interface LiveStockState {
  serverStock: Record<string, number>;   // productId → server currentStock (last pull)
  serverBatchQty: Record<string, number>; // batchId   → server quantity (last pull)
  overlay: Record<string, number>;        // productId → unsynced movement delta
  batchOverlay: Record<string, number>;   // batchId   → unsynced movement delta
  setServerStock: (next: Record<string, number>) => void;
  setServerBatchQty: (next: Record<string, number>) => void;
  setOverlay: (next: Record<string, number>) => void;
  setBatchOverlay: (next: Record<string, number>) => void;
}

export const useLiveStockStore = create<LiveStockState>((set) => ({
  serverStock: {},
  serverBatchQty: {},
  overlay: {},
  batchOverlay: {},
  setServerStock: (serverStock) => set({ serverStock }),
  setServerBatchQty: (serverBatchQty) => set({ serverBatchQty }),
  setOverlay: (overlay) => set({ overlay }),
  setBatchOverlay: (batchOverlay) => set({ batchOverlay }),
}));

/** Pure combiner: server snapshot + overlay, or the optimistic fallback if unknown. */
export function combineLiveStock(
  id: string,
  optimistic: number,
  serverSnapshot: Record<string, number>,
  overlay: Record<string, number>,
): number {
  if (!(id in serverSnapshot)) return optimistic; // no snapshot yet → optimistic
  return Math.max(0, serverSnapshot[id] + (overlay[id] ?? 0));
}

/** Non-reactive reads (for imperative call sites). */
export function liveStock(productId: string, currentStock: number): number {
  const { serverStock, overlay } = useLiveStockStore.getState();
  return combineLiveStock(productId, currentStock, serverStock, overlay);
}
export function liveBatchQty(batchId: string, quantity: number): number {
  const { serverBatchQty, batchOverlay } = useLiveStockStore.getState();
  return combineLiveStock(batchId, quantity, serverBatchQty, batchOverlay);
}

/** Reactive hooks for display components. */
export function useLiveStock(productId: string, currentStock: number): number {
  return useLiveStockStore((s) => combineLiveStock(productId, currentStock, s.serverStock, s.overlay));
}
export function useLiveBatchQty(batchId: string, quantity: number): number {
  return useLiveStockStore((s) => combineLiveStock(batchId, quantity, s.serverBatchQty, s.batchOverlay));
}

/** Merge server values captured during a pull into the in-memory snapshots. */
export function applyServerStock(captured: Record<string, number>): void {
  if (Object.keys(captured).length === 0) return;
  const cur = useLiveStockStore.getState().serverStock;
  useLiveStockStore.getState().setServerStock({ ...cur, ...captured });
}
export function applyServerBatchQty(captured: Record<string, number>): void {
  if (Object.keys(captured).length === 0) return;
  const cur = useLiveStockStore.getState().serverBatchQty;
  useLiveStockStore.getState().setServerBatchQty({ ...cur, ...captured });
}

/**
 * Recompute the product + batch overlays from this device's UNSYNCED stock movements.
 * Call after a sale (instant own-sale display) and after a sync (some may now be synced).
 */
export async function refreshOverlay(): Promise<void> {
  try {
    const unsynced = await database
      .get<StockMovement>('stock_movements')
      .query(Q.where('_status', Q.oneOf(['created', 'updated'])))
      .fetch();
    const overlay: Record<string, number> = {};
    const batchOverlay: Record<string, number> = {};
    for (const m of unsynced) {
      const pd = productStockDelta(m.type, m.quantity);
      if (pd !== 0) overlay[m.productId] = (overlay[m.productId] ?? 0) + pd;
      if (m.batchId) {
        const bd = batchStockDelta(m.type, m.quantity);
        if (bd !== 0) batchOverlay[m.batchId] = (batchOverlay[m.batchId] ?? 0) + bd;
      }
    }
    useLiveStockStore.getState().setOverlay(overlay);
    useLiveStockStore.getState().setBatchOverlay(batchOverlay);
  } catch (e) {
    console.warn('[liveStock] overlay refresh failed (non-fatal):', e);
  }
}
